<template>
  <div class="w-full mx-auto bg-white p-0">
    <!-- Total 3 Year Fundraising Goal -->
    <div class="bg-[#FAF8F4] border border-[#00423D] rounded-[8.5px] mx-6 mt-6 flex items-center justify-between px-8 py-3" style="min-height:62px;">
      <div class="text-[#00423D] font-semibold text-[16px] uppercase font-open-sans">Total 3 Year Fundraising Goal</div>
      <div class="text-[#00423D] font-semibold font-minion-pro text-[30px]">${{ formatNumber(total3YearGoal) }}</div>
    </div>

    <!-- Yearly Breakdown -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-0 mt-8 px-6">
      <div v-for="(year, idx) in years" :key="idx" class="flex flex-col items-center relative px-2">
        <!-- Top Label and Divider -->
        <div class="w-full flex flex-col items-center">
          <div class="text-[#414141] font-semibold text-[12px] uppercase font-open-sans tracking-wide text-left w-full">Year {{ idx + 1 }}</div>
          <div class="w-full h-[1px] bg-[#00423D] opacity-50 mt-1 mb-4"></div>
        </div>
        <!-- Expenses -->
        <div class="w-full flex flex-col items-center mb-2">
          <div class="text-[12px] font-semibold uppercase font-open-sans text-[#3E02A0] text-right w-full pr-4">Total Annual Expenses</div>
          <div class="text-[25px] font-minion-pro font-semibold text-[#3E02A0] leading-none text-right w-full pr-4">${{ formatNumber(year.expenses) }}</div>
        </div>
        <!-- Earned Revenue -->
        <div class="w-full flex flex-col items-center mb-2">
          <div class="text-[12px] font-semibold uppercase font-open-sans text-[#00423D] text-right w-full pr-4">Earned Revenue</div>
          <div class="text-[25px] font-minion-pro font-semibold text-[#00423D] leading-none text-right w-full pr-4">${{ formatNumber(year.earnedRevenue) }}</div>
        </div>
        <!-- Investment Income -->
        <div class="w-full flex flex-col items-center mb-2">
          <div class="text-[12px] font-semibold uppercase font-open-sans text-[#00423D] text-right w-full pr-4">Investment Income</div>
          <div class="text-[25px] font-minion-pro font-semibold text-[#00423D] leading-none text-right w-full pr-4">${{ formatNumber(year.investmentIncome) }}</div>
        </div>
        <!-- Gov. Appropriations -->
        <div class="w-full flex flex-col items-center mb-2">
          <div class="text-[12px] font-semibold uppercase font-open-sans text-[#00423D] text-right w-full pr-4">Gov. Appropriations</div>
          <div class="text-[25px] font-minion-pro font-semibold text-[#00423D] leading-none text-right w-full pr-4">${{ formatNumber(year.govAppropriations) }}</div>
        </div>
        <!-- In-Kind Goods & Services with minus icon -->
        <div class="w-full flex flex-col items-center mb-2">
          <div class="text-[12px] font-semibold uppercase font-open-sans text-[#00423D] text-right w-full pr-4">In-Kind Goods & Services</div>
          <div class="flex items-center justify-end w-full pr-4">
            <span class="text-[32px] font-minion-pro font-semibold text-[#00423D] leading-none text-left w-full ml-6" style="line-height: 1;">&ndash;</span>
            <span class="text-[25px] font-minion-pro font-semibold text-[#00423D] leading-none">${{ formatNumber(year.inKindGoods) }}</span>
          </div>
        </div>
        <!-- Divider between in-kind and annual goal -->
        <div class="w-full border-t-[1.5px] border-[#00423D] my-2"></div>
        <!-- Bottom Card for Annual Goal -->
        <div class="w-full flex flex-col items-center mt-2">
          <div class="bg-[#00423D] border border-[#00423D] rounded-[12px] w-full flex flex-col items-center py-2">
            <div class="text-[12px] font-semibold uppercase font-open-sans text-[#FAF8F4]">Year {{ idx + 1 }} Annual Fundraising Goal</div>
            <div class="text-[25px] font-minion-pro font-semibold text-[#FAF8F4] leading-none">${{ formatNumber(year.annualGoal) }}</div>
          </div>
        </div>
      </div>
    </div>
    <div class="mb-6"></div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  // Accepts an array of 3 years of data, each with the required fields
  yearsData: {
    type: Array,
    default: () => [
      {
        expenses: 120000,
        earnedRevenue: 120000,
        investmentIncome: 120000,
        govAppropriations: 120000,
        inKindGoods: 120000,
      },
      {
        expenses: 120000,
        earnedRevenue: 120000,
        investmentIncome: 120000,
        govAppropriations: 120000,
        inKindGoods: 120000,
      },
      {
        expenses: 120000,
        earnedRevenue: 120000,
        investmentIncome: 120000,
        govAppropriations: 120000,
        inKindGoods: 120000,
      },
    ],
  },
});

// Calculate annual fundraising goal for each year
const years = computed(() => {
  return props.yearsData.map((year) => {
    // Annual Fundraising Goal = Expenses - (Earned Revenue + Investment Income + Gov. Appropriations + In-Kind Goods)
    const annualGoal = year.expenses - (
      (year.earnedRevenue || 0) +
      (year.investmentIncome || 0) +
      (year.govAppropriations || 0) +
      (year.inKindGoods || 0)
    );
    return {
      ...year,
      annualGoal, // Allow negative values
    };
  });
});

const total3YearGoal = computed(() => {
  return years.value.reduce((sum, y) => sum + (y.annualGoal || 0), 0);
});

function formatNumber(num) {
  return (num || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
</script>

<style scoped>
.font-minion-pro {
  font-family: 'Minion Pro', serif;
}
.font-open-sans {
  font-family: 'Open Sans', sans-serif;
}
</style> 